import { hurtPlayer } from "./combat";
import { addItem } from "./inventory";
import { los, moveCircle, norm } from "./physics";
import { hashSeed } from "./rng";
import { generateHollow } from "../world/hollowGen";
import { ENEMY_RADIUS, Tile, type Enemy, type WorldState } from "./types";

export function enterHollow(w: WorldState, depth = 1): void {
  w.player.overworldX = w.player.x;
  w.player.overworldY = w.player.y;
  const seed = hashSeed(w.seed, "hollow", depth, w.extracts);
  w.hollow = generateHollow(seed, depth);
  w.hollow.enteredAt = w.timeSec;
  w.mode = "hollow";
  w.player.x = w.hollow.start.x;
  w.player.y = w.hollow.start.y;
  w.player.vx = 0;
  w.player.vy = 0;
  w.knowledge.hollowEntered = true;
  w.knowledge.deepest = Math.max(w.knowledge.deepest, depth);
  if (w.weather === "storm" || w.weather === "rain") {
    for (const e of w.hollow.enemies) e.alert = Math.min(1, e.alert + (w.weather === "storm" ? 0.45 : 0.2));
    say(w, w.weather === "storm" ? "The Hollow is already awake. It heard the rain." : "Drips follow you down. Something else is listening.");
  } else {
    say(w, depth === 1 ? "The well does not echo." : `The Hollow accepts you at depth ${depth}.`);
  }
}

export function extractHollow(w: WorldState): void {
  if (!w.hollow) return;
  const depth = w.hollow.depth;
  const carried = w.bag.some((s) => s.id === "artifact" || s.id === "cinder" || s.id === "strange_root");
  w.extracts += 1;
  w.knowledge.extractedOnce = true;
  w.timeSec += 900 * depth;
  w.mode = "overworld";
  w.player.x = w.overworld.well.x + 0.5;
  w.player.y = w.overworld.well.y + 1.4;
  w.player.vx = 0;
  w.player.vy = 0;
  w.hollow = null;
  if (w.weather === "clear") w.weather = "overcast";

  w.memory.push({
    x: w.overworld.well.x + 0.6,
    y: w.overworld.well.y + 1.3,
    kind: "ash",
    age: 0,
    life: 1,
    hue: 0.16,
    layer: "overworld",
  });
  if (depth > 1) {
    w.memory.push({
      x: w.overworld.well.x + 1.4,
      y: w.overworld.well.y + 2.2,
      kind: "blood",
      age: 0,
      life: 1,
      hue: 0.06,
      layer: "overworld",
    });
  }
  const mouth = w.overworld.pois.mouth;
  if (mouth && w.overworld.w) {
    const i = (mouth.y | 0) * w.overworld.w + (mouth.x | 0);
    if (i >= 0 && i < w.overworld.tiles.length) w.overworld.tiles[i] = Tile.Well;
  }

  if (carried) {
    const bed = w.overworld.crops[0];
    if (bed) bed.contamination = Math.min(1, bed.contamination + 0.25 * depth);
    w.home.dirt = Math.min(1, w.home.dirt + 0.08);
  }

  const pike = w.npcs.find((n) => n.id === "pike");
  if (pike) {
    pike.x = w.overworld.well.x + 0.8;
    pike.y = w.overworld.well.y + 1.6;
    pike.mood -= 0.08 * depth;
    pike.memory.playerDepth = Math.max(pike.memory.playerDepth, depth);
  }
  const mara = w.npcs.find((n) => n.id === "mara");
  if (mara) mara.memory.playerDepth = Math.max(mara.memory.playerDepth, depth);

  const wardens = w.factions.find((f) => f.id === "wardens");
  if (wardens) wardens.control = Math.min(1, wardens.control + 0.04 * depth);

  say(
    w,
    depth > 1
      ? "You come up later than you meant. The well-mouth is stained. Pike is already at the lip."
      : "Night air. Ash on the bricks. The square is not how you left it.",
  );
}

export function descendFurther(w: WorldState): void {
  if (!w.hollow) return;
  enterHollow(w, w.hollow.depth + 1);
}

export function tickEnemies(w: WorldState, dt: number): void {
  if (!w.hollow) return;
  const { tiles, w: W, h: H } = w.hollow;
  const p = w.player;
  for (const e of w.hollow.enemies) {
    if (e.state === "dead" || e.hp <= 0) continue;
    e.stun = Math.max(0, e.stun - dt);
    if (e.stun > 0) {
      const m = moveCircle(tiles, W, H, e, { x: e.vx, y: e.vy }, dt, e.radius);
      e.x = m.x;
      e.y = m.y;
      e.vx *= 0.85;
      e.vy *= 0.85;
      continue;
    }

    const d = Math.hypot(p.x - e.x, p.y - e.y);
    const sees = d < hearRange(e, w) && los(tiles, W, H, e.x, e.y, p.x, p.y);
    const bloodNear = w.memory.some(
      (m) => m.kind === "blood" && m.layer === "hollow" && Math.hypot(m.x - e.x, m.y - e.y) < 3,
    );

    if (sees || (e.kind === "lurcher" && bloodNear && d < 7)) e.alert = Math.min(1, e.alert + dt * 0.9);
    else e.alert = Math.max(0, e.alert - dt * 0.15);

    if (e.alert > 0.35) e.state = e.kind === "coward" && p.lantern ? "flee" : "hunt";
    else if (e.alert > 0.1) e.state = "suspect";
    else e.state = "idle";

    let wx = 0;
    let wy = 0;
    if (e.state === "idle") {
      wx = Math.sin(w.timeSec * 0.3 + e.id) * 0.4;
      wy = Math.cos(w.timeSec * 0.25 + e.id) * 0.4;
    } else if (e.state === "suspect") {
      wx = (p.x - e.x) * 0.15;
      wy = (p.y - e.y) * 0.15;
    } else if (e.state === "flee") {
      wx = e.x - p.x;
      wy = e.y - p.y;
    } else if (e.state === "hunt") {
      wx = p.x - e.x;
      wy = p.y - e.y;
    }
    const n = norm(wx, wy);
    const spd = speedOf(e);
    e.vx = n.x * spd;
    e.vy = n.y * spd;
    if (n.x || n.y) e.facing = n;
    const m = moveCircle(tiles, W, H, e, { x: e.vx, y: e.vy }, dt, ENEMY_RADIUS);
    e.x = m.x;
    e.y = m.y;

    if (e.state === "hunt" && d < 0.72 && e.wind <= 0) {
      e.wind = 0.28;
    }
    if (e.wind > 0) {
      e.wind -= dt;
      if (e.wind <= 0 && d < 0.85) hurtPlayer(w, damageOf(e), e);
    }
  }
}

function hearRange(e: Enemy, w: WorldState): number {
  let r = 6.5;
  if (e.kind === "listener") r = 9;
  else if (e.kind === "watcher") r = 11;
  else if (e.kind === "skitter") r = 5;
  if (!w.player.lantern || w.player.oil <= 0) r *= 1.45;
  return r;
}

function speedOf(e: Enemy): number {
  if (e.kind === "skitter") return 3.4;
  if (e.kind === "coward") return 2.8;
  if (e.kind === "watcher") return 1.6;
  if (e.kind === "vein") return 1.4;
  return 2.15;
}

function damageOf(e: Enemy): number {
  if (e.kind === "vein") return 16;
  if (e.kind === "lurcher") return 12;
  return 8;
}

export function pickupNear(w: WorldState): boolean {
  if (!w.hollow) return false;
  for (const l of w.hollow.loot) {
    if (l.taken) continue;
    if (Math.hypot(l.x - w.player.x, l.y - w.player.y) < 0.85) {
      if (addItem(w.bag, { ...l.item })) {
        l.taken = true;
        say(w, `Took ${l.item.id.replace(/_/g, " ")}.`);
        return true;
      }
    }
  }
  for (const lm of w.hollow.landmarks) {
    if (lm.read) continue;
    if (Math.hypot(lm.x - w.player.x, lm.y - w.player.y) < 1.1) {
      lm.read = true;
      w.knowledge.plates += 1;
      say(w, lm.title);
      return true;
    }
  }
  return false;
}

export function say(w: WorldState, text: string): void {
  w.msg = text;
  w.msgT = 4.2;
  w.log.unshift({ t: w.timeSec, text, kind: "story" });
  if (w.log.length > 40) w.log.pop();
}
